import { useQuery } from '@provision/graphql-client';
import { useEffect } from 'react';
import { GET_BACKGROUND_JOB_QUERY } from 'src/graphql/entities/background-job/background-job.operations';
import {
  GetBackgroundJobResult,
  GetBackgroundJobVars,
} from 'src/graphql/entities/background-job/background-job.types';

const DEFAULT_POLL_INTERVAL_MS = 2_000;

/**
 * Loads a background job and polls it until it finishes. Returns `job: null` when the job does
 * not exist or is not visible to the current user; such a job is not polled.
 */
export const useBackgroundJob = (
  jobId: string | null | undefined,
  options?: { pollIntervalMs?: number },
) => {
  const pollIntervalMs = options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<
    GetBackgroundJobResult,
    GetBackgroundJobVars
  >(GET_BACKGROUND_JOB_QUERY, {
    skip: !jobId,
    variables: jobId ? { params: { jobId } } : undefined,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    context: { ttl: 0 },
  });

  const job = data?.getBackgroundJob ?? null;
  const isFinished = Boolean(job?.completedAt);
  // keep polling until a response settles it: the job finished, or it is missing or denied
  const shouldPoll = Boolean(jobId) && !(data && (!job || isFinished));

  useEffect(() => {
    if (shouldPoll) {
      startPolling(pollIntervalMs);
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [shouldPoll, pollIntervalMs, startPolling, stopPolling]);

  return {
    job,
    isFinished,
    loading: loading && !job,
    error,
    refetch,
  };
};
